// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useTranslation } from "react-i18next";
import "./Navbar.scss"
import { Link, useLocation } from "react-router-dom"
import React, { useContext, useEffect } from 'react';
import { firestore } from "../../context/FirebaseConfig";
import { getNumRequests } from "../../shared_components/LabelingRequests/LabelingRequests";

// Importing icons
import uc_logo from '../../assets/logos/logo_uc_technology_ltd_S.png'
import uc_logo_small from '../../assets/logos/logo_uc_blues_on_white.svg'
import dashboard_icon from '../../assets/icons/gray/dashboard.svg';
import settings_icon from '../../assets/icons/gray/settings.png';
import labeling_icon from '../../assets/icons/gray/labelling.png';
import bale_icon from '../../assets/icons/gray/bale_analysis.png';
import truck_icon from '../../assets/icons/gray/truck.png';
import material_flow_icon from '../../assets/icons/gray/material_flow.png';
import device_status_icon from '../../assets/icons/gray/device_status.png';
import supplier_insights_icon from '../../assets/icons/gray/supplier_insights.png';
import log_insights_icon from '../../assets/icons/gray/analytics.svg';
// imports for TEST 1 //
import { AuthContext} from "../../context/AuthContext";

const Sidebar = React.forwardRef((props, ref) => {
    // translation tools
    const { i18n, t } = useTranslation();
    const [ sidebarOpen, setSidebarOpen ] = React.useState(() => {
        // Read initial state from localStorage, default to true (open) if not found
        const savedState = localStorage.getItem('sidebarOpen');
        return savedState !== null ? (savedState === 'true' || JSON.parse(savedState)) : true;
    });
    const [ labelingRequestsNr, setLabelingRequestsNr ] = React.useState(0);
    const location = useLocation();
    const { currentUser, logEventCustom } = useContext(AuthContext);

    // Save sidebar state to localStorage when it changes
    React.useEffect(() => {
        localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
    }, [sidebarOpen]);
    
    // Initialize sidebar state for first-time users
    React.useEffect(() => {
        // If there's no stored value, initialize to open
        if (localStorage.getItem('sidebarOpen') === null) {
            localStorage.setItem('sidebarOpen', 'true');
            setSidebarOpen(true); // Also update state directly
        }
    }, []);

    // Check if current path matches link path for highlighting the active page
    const isActive = (path) => {
        return location.pathname === path;
    };

    React.useImperativeHandle(ref, () => ({
        toggleSidebar(status = null) {
            if (status !== null) {
                setSidebarOpen(status);
            } else {
                setSidebarOpen(prev => !prev);
            }
        }
    }));

    // Update parent elements with sidebar state
    useEffect(() => {
        // Add generic container class to body
        document.body.classList.add('has-sidebar');
        
        // Add or remove collapsed state class
        if (!sidebarOpen) {
            document.body.classList.add('sidebar-collapsed');
        } else {
            document.body.classList.remove('sidebar-collapsed');
        }
        
        // Apply to all container types
        const pageContainers = document.querySelectorAll('.overview, .main-content, .labeling, .deliveries, .plant-feed, .admin-settings, .material-flow, .device-statuses, .supplier-insights, .no-role');
        if (pageContainers.length > 0) {
            pageContainers.forEach(element => {
                if (!sidebarOpen) {
                    element.classList.add('sidebar-collapsed');
                } else {
                    element.classList.remove('sidebar-collapsed');
                }
            });
        }
        
        // Cleanup function
        return () => {
            document.body.classList.remove('sidebar-collapsed');
            document.body.classList.remove('has-sidebar');
        };
    }, [sidebarOpen]);

    React.useEffect(() => {
        if (['admin','labeler'].some(flag => currentUser.info.user_flags.includes(flag))) {
            getNumRequests(firestore, currentUser.company.company, currentUser.uid).then(nr => {
                setLabelingRequestsNr(nr);
            });
        }
    }, []);

    // SVG chevron icons for toggle functionality
    const ChevronLeft = () => (
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        >
            <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
    );
    
    const ChevronRight = () => (
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        >
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    );

    return (
        <div className={`sidebar-wrapper ${sidebarOpen ? "" : "sidebar-closed"}`}>
            <div className={`sidebar ${sidebarOpen ? "" : "sidebar-closed"}`}>
                <div className="sidebar--header">
                    <Link className="sidebar--logo-wrapper" to="/" onClick={() => logEventCustom("page_view", {page_title: 'home'})}>
                        {sidebarOpen ? (
                            <img src={uc_logo} className="sidebar--logo" draggable="false" alt="UpCircle Logo" />
                        ) : (
                            <img src={uc_logo_small} className="sidebar--logo sidebar--logo-small" draggable="false" alt="UpCircle Logo" />
                        )}
                    </Link>
                    
                    {sidebarOpen && (
                        <button 
                            className="sidebar--toggle-button sidebar--collapse-button" 
                            onClick={() => setSidebarOpen(false)}
                            aria-label="Collapse sidebar"
                        >
                            <ChevronLeft />
                        </button>
                    )}
                </div>
                
                {!sidebarOpen && (
                    <button 
                        className="sidebar--toggle-button sidebar--expand-button" 
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Expand sidebar"
                    >
                        <ChevronRight />
                    </button>
                )}
                
                {currentUser && currentUser.info && currentUser.info.user_flags && (
                    <div className="sidebar--content">
                        {/* Standard navigation section */}
                        <div className="sidebar--section">
                            <div className="sidebar--section-title">
                                {sidebarOpen && <span>{t("sidebar_navigation")}</span>}
                            </div>
                            <ul className="sidebar--links">
                                {['admin','manager'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/overview" 
                                            className={isActive("/overview") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'overview'})}
                                            title={t("sidebar_dashboard")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={dashboard_icon} alt="dashboard icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_dashboard")}</span>}
                                        </Link>
                                    </li>
                                )}
                                
                                {['admin','manager'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/deliveries" 
                                            className={isActive("/deliveries") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'deliveries'})}
                                            title={t("sidebar_deliveries")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={truck_icon} alt="deliveries icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_deliveries")}</span>}
                                        </Link>
                                    </li>
                                )}
                                
                                {['admin','manager','labeler'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/plant-feed" 
                                            className={isActive("/plant-feed") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'plant-feed'})}
                                            title={t("sidebar_plant_feed")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={bale_icon} alt="plant feed icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_plant_feed")}</span>}
                                        </Link>
                                    </li>
                                )}
                                
                                {['admin','manager','labeler'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/material-flow" 
                                            className={isActive("/material-flow") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'material-flow'})}
                                            title={t("sidebar_material_flow")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={material_flow_icon} alt="material flow icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_material_flow")}</span>}
                                        </Link>
                                    </li>
                                )}

                                {['admin','manager', 'beta'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/supplier-insights" 
                                            className={isActive("/supplier-insights") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'supplier-insights'})}
                                            title={t("sidebar_supplier_insights")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={supplier_insights_icon} alt="supplier insights icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_supplier_insights")}</span>}
                                        </Link>
                                    </li>
                                )}
                                
                                {['admin','manager','labeler'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                                    <li>
                                        <Link 
                                            to="/labeling" 
                                            className={isActive("/labeling") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'labeling'})}
                                            title={t("sidebar_labeling")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={labeling_icon} alt="labeling icon"/>
                                                {labelingRequestsNr > 0 && (
                                                    <div className="sidebar--notification-badge">{labelingRequestsNr}</div>
                                                )}
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_labeling")}</span>}
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </div>
                        
                        {/* Admin section (only visible for admins) */}
                        {['admin'].some(flag => currentUser.info.user_flags.includes(flag)) && (
                            <div className="sidebar--section sidebar--admin-section">
                                <div className="sidebar--section-title">
                                    {sidebarOpen && <span>{t("sidebar_admin")}</span>}
                                </div>
                                <ul className="sidebar--links">
                                    <li>
                                        <Link 
                                            to="/device-statuses" 
                                            className={isActive("/device-statuses") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'device-statuses'})}
                                            title={t("sidebar_device_statuses")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={device_status_icon} alt="device status icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_device_statuses")}</span>}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to="/log-insights" 
                                            className={isActive("/log-insights") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'log-insights'})}
                                            title={t("sidebar_log_insights")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={log_insights_icon} alt="log insights icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_log_insights")}</span>}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to="/admin-settings" 
                                            className={isActive("/admin-settings") ? "active" : ""}
                                            onClick={() => logEventCustom("page_view", {page_title: 'admin-settings'})}
                                            title={t("sidebar_admin_settings")}
                                        >
                                            <div className="sidebar--icon-container">
                                                <img src={settings_icon} alt="settings icon"/>
                                            </div>
                                            {sidebarOpen && <span>{t("sidebar_admin_settings")}</span>}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

export default Sidebar;